import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./ProjectsStrip.css";

function isVideoSrc(src) {
  const s = (src || "").toLowerCase();
  return s.endsWith(".mp4") || s.endsWith(".webm") || s.endsWith(".mov");
}

function clampIndex(i, len) {
  if (!len) return 0;
  return (i + len) % len;
}

function useTenSecondPreview({ videoRef, activeKey, enabled }) {
  const [ended, setEnded] = useState(false);
  const timerRef = useRef(null);

  const clear = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  };

  const restartAndPlay = useCallback(async () => {
    const v = videoRef.current;
    if (!v) return;

    clear();
    setEnded(false);

    try {
      v.currentTime = 0;
    } catch {}

    try {
      const pp = v.play?.();
      if (pp?.catch) pp.catch(() => {});
    } catch {}

    timerRef.current = setTimeout(() => {
      const vv = videoRef.current;
      if (!vv) return;
      try {
        vv.pause?.();
      } catch {}
      setEnded(true);
    }, 10_000);
  }, [videoRef]);

  useEffect(() => {
    if (!enabled) return;
    restartAndPlay();
    return () => clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey, enabled]);

  const replay = useCallback(() => restartAndPlay(), [restartAndPlay]);

  return { ended, replay };
}

export default function ProjectsStrip() {
  const GCS_BASE = "https://storage.googleapis.com/my-portfolio-media";

  const projects = useMemo(
    () => [
      {
        key: "hydromind",
        title: "HydroMind",
        subtitle: "Automated hydroponics control system",
        tag: "Smart Automation",
        audience: "Home growers & small farms",
        desc:
          "A control dashboard that monitors sensors and automates dosing/pumps so plants stay healthy with minimal manual work.",
        highlights: [
          "Live sensor monitoring (pH, nutrients, temperature)",
          "Automated dosing schedules + safety checks",
          "Hardware + software built as a complete product",
        ],
        stack: ["React", "Flask", "MySQL", "Cloud Run", "Cloud SQL", "KiCad"],
        media: `${GCS_BASE}/hydromind.mp4`,
      },
      {
        key: "sahar-watches",
        title: "Sahar Watches",
        subtitle: "Luxury watch showroom & operations platform",
        tag: "E-commerce Ops",
        audience: "Watch dealer - Private Customer",
        desc:
          "A platform to manage inventory, customers, and sales workflows for a premium watch dealership.",
        highlights: [
          "Inventory + customer management",
          "Sales workflows & client follow-ups",
          "Images and media organized in the cloud",
        ],
        stack: ["React", "Flask", "MySQL", "Google Cloud Storage"],
        media: `${GCS_BASE}/sahar-watches.mp4`,
      },
      {
        key: "asx",
        title: "All Star Xperience",
        subtitle: "Soccer league website & stats hub",
        tag: "Sports Platform",
        audience: "Players, teams, fans - Private Customer",
        desc:
          "A league platform that shows schedules, standings, and top scorers with a clean, modern experience.",
        highlights: [
          "Standings, schedules, and leaderboards",
          "Simple admin-friendly data updates",
          "Mobile-first UI for players",
        ],
        stack: ["React", "Apps Script"],
        media: `${GCS_BASE}/asx.mp4`,
      },
    ],
    []
  );

  const [idx, setIdx] = useState(0);
  const active = projects[idx];

  const videoRef = useRef(null);
  const activeIsVideo = isVideoSrc(active?.media);

  const { ended: previewEnded, replay } = useTenSecondPreview({
    videoRef,
    activeKey: active?.key,
    enabled: !!activeIsVideo,
  });

  const goTo = (i) => setIdx(clampIndex(i, projects.length));
  // eslint-disable-next-line
  const goNext = useCallback(() => goTo(idx + 1), [idx, projects.length]);
  // eslint-disable-next-line
  const goPrev = useCallback(() => goTo(idx - 1), [idx, projects.length]);

  // keyboard
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goNext, goPrev]);

  // swipe
  const drag = useRef({ down: false, x0: 0, dx: 0 });
  const onPointerDown = (e) => {
    drag.current.down = true;
    drag.current.x0 = e.clientX;
    drag.current.dx = 0;
  };
  const onPointerMove = (e) => {
    if (!drag.current.down) return;
    drag.current.dx = e.clientX - drag.current.x0;
  };
  const onPointerUp = () => {
    if (!drag.current.down) return;
    const dx = drag.current.dx;
    drag.current.down = false;
    if (Math.abs(dx) > 60) (dx < 0 ? goNext : goPrev)();
  };

  if (!active) return null;

  return (
    <section className="pstrip" id="projects" aria-label="Projects">
      {/* full-bleed wrapper */}
      <div className="pstrip__wrap">
        <div className="pstrip__top">
          <div className="pstrip__title">Projects</div>

          <div className="pstrip__topRight">
            <div className="pstrip__nav">
              <button className="pstrip__btn" onClick={goPrev} aria-label="Previous project">
                ←
              </button>
              <button className="pstrip__btn" onClick={goNext} aria-label="Next project">
                →
              </button>
            </div>

            <Link
              className="pstrip__viewall"
              to="/projects"
              onClick={() => window.scrollTo(0, 0)}
            >
              View all →
            </Link>

          </div>
        </div>

        <div
          className="pstrip__stage"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {/* Media */}
          <div className="pstrip__media" aria-label={`${active.title} media`}>
            {activeIsVideo ? (
              <video
                ref={videoRef}
                className="pstrip__video"
                src={active.media}
                muted
                playsInline
                preload="metadata"
              />
            ) : (
              <img className="pstrip__img" src={active.media} alt={active.title} />
            )}

            {activeIsVideo && (
              <div className="pstrip__preview">
                <div className={`pstrip__bar ${previewEnded ? "is-ended" : "is-running"}`} />
                <div className="pstrip__previewText">
                  {previewEnded ? "Preview ended (10s)" : "Playing 10s preview…"}
                </div>
                {previewEnded && (
                  <button className="pstrip__replay" type="button" onClick={replay}>
                    Replay 10s
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Details (simple column, not a “card”) */}
          <div className="pstrip__info" aria-label={`${active.title} details`}>
            <div className="pstrip__tagRow">
              <span className="pstrip__tag">{active.tag}</span>
              <span className="pstrip__count">
                <b>{idx + 1}</b> / {projects.length}
              </span>
            </div>

            <div className="pstrip__name">{active.title}</div>
            <div className="pstrip__sub">{active.subtitle}</div>

            <div className="pstrip__meta">
              <span className="pstrip__metaLabel">Built for</span>
              <span className="pstrip__metaValue">{active.audience}</span>
            </div>

            <div className="pstrip__desc">{active.desc}</div>

            <ul className="pstrip__bullets" aria-label="Key highlights">
              {(active.highlights || []).slice(0, 3).map((h) => (
                <li key={h}>{h}</li>
              ))}
            </ul>

            <div className="pstrip__tools">
              <div className="pstrip__toolsLabel">Tools</div>
              <div className="pstrip__chips">
                {active.stack.slice(0, 8).map((s) => (
                  <span key={s} className="pstrip__chip">
                    {s}
                  </span>
                ))}
              </div>
            </div>

            <div className="pstrip__dots" aria-label="Project dots">
              {projects.map((p, i) => (
                <button
                  key={p.key}
                  type="button"
                  className={`pstrip__dot ${i === idx ? "active" : ""}`}
                  onClick={() => goTo(i)}
                  aria-label={`Go to ${p.title}`}
                />
              ))}
            </div>

            <div className="pstrip__openRow">
              <Link className="pstrip__open" to="/projects" aria-label={`Open ${active.title}`}  onClick={() => window.scrollTo(0, 0)} >
                Open project →
              </Link>
              <div className="pstrip__hint">Swipe or use ← →</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
